import React, { useRef } from 'react';
import { useRouter } from 'next/router';
import { Text } from '@ui/atoms';
import { cn } from '@ui/atoms/utils/utils';
import { triggerCustomGaEvent } from '@helpers/miscelleanous';
import { bondDirectoryGaEventsName } from '@helpers/ga-events-name-enum';
import { useBondDirectoryContext } from '../context/bond-directory-context';
import InfiniteScroll from 'react-infinite-scroll-component';
import routes from '@helpers/routes';

const BondResultsMobile = () => {
    const router = useRouter();
    const scrollableDivMobileRef = useRef(null);

    const {
        loading,
        searchError,
        fetchSearchResults,
        fetchNextSuggestions,
        hasMore,
        results,
        errors,
    } = useBondDirectoryContext(({ states, services }) => ({
        loading: states.loading.search,
        searchError: states.searchError,
        hasMore: states.hasMore,
        fetchSearchResults: services.fetchSearchResults,
        fetchNextSuggestions: services.fetchNextSuggestions,
        results: states.suggestions,
        errors: states.errors,
    }));

    const handleGoToBondDetails = (isin, issuer) => {
        triggerCustomGaEvent(
            bondDirectoryGaEventsName.CLICKED_BOND_DIRECTORY_SEARCH_RESULT,
            { isin, issuer_name: issuer }
        );
        if (isin) {
            router.push(routes.bonds.byIsin(isin));
        } else {
            fetchSearchResults(
                {
                    value: issuer,
                    pageNumber: 1,
                },
                false
            );
        }
    };

    const containerClass =
        'flex items-center p-4 transition-all ease-in-out border-b border-gray-200 cursor-pointer bg-basicWhite group flex-1 ';
    const textClass = 'text-gray-700 p4-regular';
    const highlightedTextClass =
        'text-secondary-600 p4-semibold md:p3-semibold';

    const highlightText = (text, highlight) => {
        if (!highlight) return text;
        const parts = text.split(new RegExp(`(${highlight})`, 'gi'));
        return parts.map((part, index) =>
            part.toLowerCase() === highlight.toLowerCase() ? (
                <span key={index} className={highlightedTextClass}>
                    {part}
                </span>
            ) : (
                part
            )
        );
    };

    return (
        <div
            id="scrollableDivMobile"
            style={{ height: '78%', overflow: 'auto' }}
            className="flex-1 mt-16 bg-basicWhite"
            ref={scrollableDivMobileRef}
        >
            <InfiniteScroll
                dataLength={results?.suggestions?.length || 0}
                next={fetchNextSuggestions}
                hasMore={hasMore}
                loader={
                    loading && (
                        <div className="flex items-center justify-center gap-3 py-3 border-b">
                            <div className={'button-loader-primary '} />
                            <Text
                                content={'Loading results'}
                                className={cn(textClass, 'text-gray-500')}
                            />
                        </div>
                    )
                }
                endMessage={
                    !loading &&
                    results?.suggestions?.length === 0 && (
                        <Text
                            className={cn(
                                textClass,
                                'w-full py-3 px-4 border-b'
                            )}
                            content="No more results to display "
                        />
                    )
                }
                scrollableTarget="scrollableDivMobile"
            >
                {searchError && (
                    <div className="flex items-center flex-1 gap-3 px-4 py-3 border-b">
                        <Text
                            content={searchError}
                            className={cn(textClass, 'text-gray-500')}
                        />
                    </div>
                )}
                {errors && results?.error && (
                    <div className="flex items-center flex-1 gap-3 px-4 py-3 border-b">
                        <Text content={results?.error} className={textClass} />
                    </div>
                )}
                {loading ? (
                    <div className="flex items-center justify-center flex-1 gap-3 py-3 border-b">
                        <div className={'button-loader-primary '} />
                        <Text
                            content={'Loading results'}
                            className={cn(textClass, 'text-gray-500')}
                        />
                    </div>
                ) : (
                    <>
                        {results?.suggestions?.map(
                            ({ isin, issuer }, index) => (
                                <div
                                    key={`${isin}-${index}`}
                                    className={cn(
                                        'hover:bg-primary-50',
                                        containerClass
                                    )}
                                    onClick={() =>
                                        handleGoToBondDetails(isin, issuer)
                                    }
                                >
                                    <Text
                                        content={
                                            isin ? (
                                                <>
                                                    {highlightText(
                                                        issuer,
                                                        results?.search
                                                    )}{' '}
                                                    -{' '}
                                                    <span className="text-secondary-600 p4-semibold md:p3-semibold ">
                                                        {isin}
                                                    </span>
                                                </>
                                            ) : (
                                                highlightText(
                                                    issuer,
                                                    results?.search
                                                )
                                            )
                                        }
                                        className={textClass}
                                    />
                                </div>
                            )
                        )}
                    </>
                )}
            </InfiniteScroll>
        </div>
    );
};

export default React.memo(BondResultsMobile);
